import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { ReleaseApproval, ReleaseDetail } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

/**
 * Giving or refusing an approval (§31).
 *
 * <p>Refusing requires a comment and the button stays disabled without one. The server refuses it
 * too — a release turned down with no reason leaves the team nothing to fix and the next reader
 * nothing to understand.
 */
export function ApprovalDecisionDrawer({
  release,
  approval,
  decision,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  approval: ReleaseApproval | null;
  decision: "APPROVED" | "REJECTED";
  onClose: () => void;
  onSaved: () => void;
}) {
  const [comment, setComment] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (approval) {
      setComment("");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approval, decision]);

  if (!approval) {
    return null;
  }

  const refusing = decision === "REJECTED";
  const canSubmit = !refusing || comment.trim() !== "";

  const submit = () =>
    void run(
      () =>
        api.post(`/api/v1/releases/${release.id}/approvals/${approval.id}/decision`, {
          decision,
          comment: comment.trim() === "" ? null : comment.trim(),
          lockVersion: approval.lockVersion,
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open
      onClose={onClose}
      title={refusing ? "Refuse this release" : "Approve this release"}
      subtitle={`${approval.roleLabel} · ${release.releaseKey}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={refusing ? "danger" : "primary"}
            onClick={submit}
            disabled={!canSubmit}
            loading={saving}
          >
            {saving ? "Recording…" : refusing ? "Refuse" : "Approve"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <p className="text-sm text-ink-600">
          {refusing
            ? "This is recorded against your name, with your reason, and the release will not be able to go until it is put right."
            : `You are approving ${release.releaseName} as ${approval.roleLabel.toLowerCase()}. This is recorded against your name.`}
        </p>

        <Field
          label={refusing ? "What needs to change" : "Comment"}
          required={refusing}
          hint={
            refusing
              ? "Without this, nobody knows what to fix."
              : "Optional, and kept with the release."
          }
        >
          <Textarea
            rows={4}
            autoFocus
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
